import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useAction } from "wasp/client/operations";
import {
  getMySocialReports,
  getSocialReportThumbnailUrl,
  renderReportImage,
  deleteSocialReport,
} from "wasp/client/operations";
import type { SocialReport } from "wasp/entities";
import { Card } from "../../client/components/ui/card";
import { Button } from "../../client/components/ui/button";
import { Trash2, Download } from "lucide-react";
import useTranslation from "../../client/hooks/useTranslation";
import { saveReportImages } from "./ReportShareActions";

export function ReportsList() {
  const t = useTranslation();
  const { data, refetch } = useQuery(getMySocialReports);
  const deleteAction = useAction(deleteSocialReport);

  async function handleDelete(id: string) {
    if (!window.confirm(t.reports.deleteConfirm)) return;
    await deleteAction({ id });
    refetch();
  }

  if (!data || data.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {data.map((r) => (
        <ReportCard key={r.id} report={r} onDelete={() => handleDelete(r.id)} />
      ))}
    </div>
  );
}

function ReportCard({
  report,
  onDelete,
}: {
  report: SocialReport;
  onDelete: () => void;
}) {
  const t = useTranslation();
  // useQuery (instead of a one-shot fetch in useEffect) so Wasp caches the
  // signed URL per report id and doesn't re-fire on every entity invalidation.
  const { data: thumb, refetch: refetchThumb } = useQuery(getSocialReportThumbnailUrl, {
    id: report.id,
  });
  const renderAction = useAction(renderReportImage);

  // RATTRAPAGE des rapports sans image.
  //
  // Les rapports crees avant le rendu immediat (et ceux du job planifie, qui
  // reste volontairement paresseux) ont des s3Keys vides :
  // getSocialReportThumbnailUrl renvoie alors null et la carte restait bloquee
  // sur son placeholder, sans aucun moyen de s'en sortir depuis cette page.
  //
  // On ne declenche le rendu QUE si la vignette est confirmee absente
  // (thumb === null, et non undefined qui signifie « requete en cours ») et une
  // SEULE fois par carte : sans ce garde, un echec de rendu relancerait l'action
  // a chaque re-rendu de React, en boucle.
  const [renderAttempted, setRenderAttempted] = useState(false);

  useEffect(() => {
    if (thumb !== null || renderAttempted) return;
    setRenderAttempted(true);
    renderAction({ id: report.id })
      .then(() => refetchThumb())
      .catch(() => {
        // Rendu impossible : on laisse le placeholder plutot que de casser la
        // liste. Le detail du rapport reste consultable.
      });
  }, [thumb, renderAttempted, renderAction, report.id, refetchThumb]);

  async function handleDownload() {
    // Le rendu est idempotent cote serveur (s3Keys deja presentes = resservies),
    // donc cet appel garantit l'existence de l'image sans jamais la reproduire.
    // Sans lui, telecharger un rapport pas encore rendu renvoyait une liste vide.
    const { signedUrls } = await renderAction({ id: report.id });
    // Mobile → camera roll via Web Share; desktop → ZIP / direct download.
    await saveReportImages(signedUrls, report.shareToken);
  }

  const createdAt = new Date(report.createdAt).toLocaleDateString();

  return (
    <Card className="overflow-hidden flex flex-col">
      <Link
        to={`/reports/${report.id}`}
        aria-label={t.reports.viewReport}
        className="bg-muted flex items-center justify-center overflow-hidden cursor-pointer transition hover:opacity-90"
        style={{ aspectRatio: report.format === "story" ? "9 / 16" : "4 / 5" }}
      >
        {thumb ? (
          <img src={thumb} alt={t.reports.thumbnailAlt} className="object-cover w-full h-full" />
        ) : (
          <span className="text-xs text-muted-foreground">…</span>
        )}
      </Link>
      <div className="p-3 space-y-2 flex-1 flex flex-col">
        <div className="text-sm font-medium">{createdAt}</div>
        <div className="text-xs text-muted-foreground capitalize">
          {report.period} · {report.format}
        </div>
        <div className="mt-auto flex gap-1">
          <Button size="sm" variant="ghost" className="flex-1" onClick={handleDownload}>
            <Download className="h-3 w-3 mr-1" />
            {t.reports.download}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            aria-label={t.reports.deleteAriaLabel}
          >
            <Trash2 className="h-3 w-3 text-destructive" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
